import { c as _c } from "react/compiler-runtime";
import { type ReactNode } from "react";
import { Flag, Ruler } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Visual primitives shared between `PrActionCards` (PR-context surface)
 * and `TaskStarterCards` (no-PR surface). Both render a centered wrapping
 * row of compact pill-shaped prompt buttons, optionally led by a
 * full-width suggestion card (onboarding entry, autofix toggle); keeping
 * the styles in one place ensures the surfaces stay visually identical
 * when design tweaks land.
 */

/**
 * Wrapping, left-aligned container for a row of prompt pills. gap-x stays
 * tight so the four PR action pills keep fitting the 528px composer column
 * on one line; gap-y is roomier so wrapped rows don't sit flush.
 */
export const PROMPT_PILL_ROW_CLASS = "flex w-full flex-wrap items-center justify-start gap-x-1 gap-y-2";

/**
 * Starter-pill row variant: wrapping (stacking) is reserved for small
 * screens where the composer column genuinely runs out of width. From
 * `sm` up the two starter pills always share one line: the composer
 * column is capped at 528px there, which fits both pills' natural
 * widths, and in the rare case a wider font pushes past that the pill
 * labels truncate (see PromptPill) instead of dropping onto a second
 * line.
 */
export const STARTER_PILL_ROW_CLASS = cn(PROMPT_PILL_ROW_CLASS, "sm:flex-nowrap");
export const RIGHTSIZE_STARTER_PILL_LAYOUT_CLASS = "grow sm:w-[270px] sm:flex-none";

/**
 * Pill chrome shared by the prompt pills and the MCP pill-cluster
 * segments (which override the rounding to form a segmented control).
 */
export const PROMPT_PILL_SEGMENT_CLASS = "flex h-[30px] cursor-pointer items-center border border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-inset-hover)] focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:border-[var(--focus-border)]";

// px-2.5 keeps the four PR action pills on one line in the 528px composer
// column (px-3 overflows it by 4px).
export const PROMPT_PILL_CLASS = cn(PROMPT_PILL_SEGMENT_CLASS, "min-w-0 gap-1.5 rounded-full px-2.5");

/**
 * Card chrome for the full-width suggestion cards rendered above the
 * pill row (onboarding entry, autofix toggle).
 */
export const SUGGESTION_CARD_CLASS = "flex w-full items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5";

/** Title + description stack used inside a suggestion card. */
export function SuggestionCardTitleBlock(t0) {
  const $ = _c(10);
  const {
    title,
    description,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex min-w-0 flex-1 flex-col", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <span className="type-body-s-medium text-neutral-900 dark:text-neutral-100">{title}</span>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== description) {
    t3 = <span className="type-body-xs-regular text-neutral-500 dark:text-neutral-400">{description}</span>;
    $[4] = description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <span className={t1}>{t2}{t3}</span>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export interface PromptPillProps {
  label: string;
  /** Icon node rendered at the start of the pill. */
  icon: ReactNode;
  /** Omitted by the page-level skeleton, whose root is `pointer-events-none`. */
  onClick?: () => void;
  className?: string;
}

/**
 * Standard prompt-backed pill used by both surfaces. Pure presentation;
 * leaves analytics and prompt dispatch to the parent's `onClick`.
 */
export function PromptPill(t0) {
  const $ = _c(11);
  const {
    label,
    icon,
    onClick,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(PROMPT_PILL_CLASS, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== icon) {
    t2 = <span className="flex size-4 shrink-0 items-center justify-center overflow-visible text-neutral-500 dark:text-neutral-400">{icon}</span>;
    $[2] = icon;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== label) {
    t3 = <span className="type-body-s-medium truncate text-neutral-700 dark:text-neutral-200">{label}</span>;
    $[4] = label;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== onClick || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <button type="button" onClick={onClick} className={t1}>{t2}{t3}</button>;
    $[6] = onClick;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}

/** Icon keys for the starter options rendered as pills. */
export type StarterOptionIcon = "rightsize" | "cleanup-flag";
export function starterPillIcon(icon: StarterOptionIcon): ReactNode {
  switch (icon) {
    case "rightsize":
      return <Ruler className="size-4" strokeWidth={1.75} />;
    case "cleanup-flag":
      return <Flag className="size-4" strokeWidth={1.75} />;
  }
}

/**
 * Static face of the lead starter option, kept with the pill primitives so
 * the page-level skeleton can paint it without importing `TaskStarterCards`
 * and its org query stack. The prompt it dispatches lives with the other
 * starter options, in `STARTER_PROMPT_CARDS`.
 */
export const RIGHTSIZE_STARTER_PILL: {
  title: string;
  icon: StarterOptionIcon;
} = {
  title: "Rightsize to optimize runners",
  icon: "rightsize"
};

/**
 * Copy for the user-level autofix toggle card, kept with the pill
 * primitives so the page-level skeleton can size its suggestion-card
 * placeholder against the real copy without importing `TaskStarterCards`
 * and its org query stack.
 */
export const AUTOFIX_OPTION = {
  title: "Autofix for your PRs",
  description: "Let [code]smith fix failing CI and bot comments on PRs you open"
} as const;

/**
 * Shimmer placeholder sized to match a resolved `PromptPill` (30px tall),
 * so the skeleton row swaps to the final pills without a layout shift.
 * `className` sizes the wrapper (e.g. `w-32`, `grow`) so flex behavior
 * matches the pill it stands in for.
 */
export function PromptPillSkeleton(t0) {
  const $ = _c(5);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("pointer-events-none flex h-[30px] w-44 items-center", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton className="h-[30px] w-full rounded-full" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className={t1}>{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * Shimmer placeholder for the Rightsize starter pill's slot. Unlike
 * `PromptPillSkeleton`, it renders the resolved pill's chrome with
 * invisible icon/label sizing content, so the slot keeps the exact
 * intrinsic width the settled `PromptPill` will have — `sm:flex-none`
 * on the wrapper then holds the resolved content width instead of an
 * arbitrary skeleton width. The second (grow) slot uses it too so both
 * shimmers share the same chrome.
 */
export function StarterPromptPillSkeleton(t0) {
  const $ = _c(7);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(PROMPT_PILL_CLASS, "pointer-events-none relative justify-center", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="flex size-4 shrink-0 items-center justify-center overflow-visible text-neutral-500 opacity-0 dark:text-neutral-400">{starterPillIcon(RIGHTSIZE_STARTER_PILL.icon)}</span>;
    t3 = <span className="type-body-s-medium truncate text-neutral-700 opacity-0 dark:text-neutral-200">{RIGHTSIZE_STARTER_PILL.title}</span>;
    t4 = <Skeleton className="absolute inset-0 rounded-full" />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1) {
    t5 = <button type="button" tabIndex={-1} aria-hidden={true} data-slot="starter-prompt-pill-skeleton" className={t1}>{t2}{t3}{t4}</button>;
    $[5] = t1;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}

/**
 * Shimmer placeholder matching the resolved autofix suggestion card. The
 * title/description stack is sized by the card's real copy rendered
 * invisibly, so the skeleton wraps to the same line count as the resolved
 * card at every width and swapping it in causes no layout shift. The
 * overlaid shimmer rows mirror the type line boxes (22px title, 16px
 * description lines); overflow-hidden clips the second description row
 * whenever the copy fits on one line.
 */
export function SuggestionCardSkeleton() {
  const $ = _c(6);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cn(SUGGESTION_CARD_CLASS, "pointer-events-none");
    t1 = <Skeleton className="size-6 shrink-0 rounded-full" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SuggestionCardTitleBlock className="invisible" title={AUTOFIX_OPTION.title} description={AUTOFIX_OPTION.description} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="flex h-[22px] shrink-0 items-center"><Skeleton className="h-3.5 w-36 rounded" /></span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="flex h-4 shrink-0 items-center"><Skeleton className="h-3 w-64 max-w-full rounded" /></span>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className={t0}>{t1}<span className="relative flex min-w-0 flex-1 flex-col">{t2}<span className="absolute inset-0 flex flex-col overflow-hidden">{t3}{t4}<span className="flex h-4 shrink-0 items-center"><Skeleton className="h-3 w-44 max-w-full rounded" /></span></span></span><Skeleton className="h-4 w-8 shrink-0 rounded-full" /><span aria-hidden={true} className="size-6 shrink-0" /></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}